/**
 * Agent chat backend — streams the XAES Analyst reply from the AIW A2A
 * general_blueprint agent via the BFF (`POST /api/agent/chat`).
 *
 * This is the sibling of `streamXaesReply` (vercel-ai.ts): same
 * `AsyncIterable<string>` contract — yields raw text deltas the chat hook
 * appends — but the request goes to the BFF instead of Anthropic. The BFF
 * holds the ZITADEL auth boundary, relays the agent's (cumulative) SSE as
 * incremental `{delta}` events, and the agent grounds on the context-engine.
 * See plans/AIW_AGENT_CHAT_INTEGRATION.md.
 *
 * Wire format from the BFF (one SSE event per line-pair):
 *   data: {"delta":"<chunk>"}     ← append to the assistant bubble
 *   data: {"error":"<message>"}   ← surfaced as a thrown Error
 *   data: {"done":true}           ← terminal marker
 *
 * Routing mirrors docs-api.ts: calls go to `VITE_DOCS_BFF_ORIGIN` (empty =
 * same-origin; prod nginx proxies `/api/*`, dev Vite proxy forwards it).
 */

import { env } from "@/env";
import { getCurrentUser } from "@/features/auth/user-manager";
import { actAsClientId } from "@/lib/active-client";
import type { CoreMessage } from "ai";

const BASE = env.VITE_DOCS_BFF_ORIGIN.replace(/\/+$/, "");

/** A retrieved document the agent's answer is grounded in, surfaced by the
 *  BFF so the UI can show a Sources panel with a quote + download link. */
export interface AgentSource {
  /** 1-based citation index — matches the `[n]` marker the agent is asked to
   *  emit when it uses this passage, so the panel can show only cited sources.
   *  See plans/AIW_AGENT_CHAT_INTEGRATION.md / ADO #460. */
  index: number;
  filename: string;
  page: string | number;
  score: number;
  /** Short excerpt from the retrieved chunk (whitespace-collapsed by the BFF). */
  quote: string;
  /** Storage tier + blob path — feed to `downloadDoc(tier, path)`. */
  tier: string;
  path: string;
  /** True when tier+path resolve to a downloadable blob. */
  downloadable: boolean;
}

export type StreamAgentReplyArgs = {
  /** Pre-composed system prompt string (see context-builder). */
  system: string;
  /** Conversation turns so far — must start with a `user` turn. */
  messages: CoreMessage[];
  /** Cancels the in-flight request on unmount or user-initiated abort. */
  signal: AbortSignal;
  /** Fires once if the BFF emits a `sources` event. `grounding` is true when
   *  the BFF searched but found no hits — lets the UI distinguish "searched,
   *  found nothing" from "grounding is off". Kept off the yielded stream so
   *  the text contract stays `AsyncIterable<string>`. */
  onSources?: (sources: AgentSource[], grounding?: boolean) => void;
  /** Active workshop id — omitted (or omit entirely) for Mission Control's
   *  multi-workshop scope. Lets the BFF attribute this turn's chat-interaction
   *  telemetry (ADO #689, flag-gated) to one workshop's Analysis view; has no
   *  effect on grounding or the reply itself. */
  workshopId?: string;
  /** Asset Locker output ids this turn's auto-context drew on (see
   *  `getReferencedOutputIds`) — same ADO #689 telemetry, ignored otherwise. */
  referencedOutputIds?: string[];
};

/** Normalise a CoreMessage's content to the plain string the BFF expects. */
function contentToString(content: CoreMessage["content"]): string {
  if (typeof content === "string") return content;
  if (Array.isArray(content)) {
    return content
      .map((p) => (p && typeof p === "object" && "text" in p ? String(p.text) : ""))
      .join("");
  }
  return "";
}

/**
 * Stream assistant text deltas from the agent (via the BFF). Yields raw
 * text chunks; the caller appends them to the in-flight assistant bubble.
 *
 * Aborts are non-fatal — `fetch` rejects with an `AbortError` when the
 * signal trips, which we swallow so the chat hook treats it as a normal
 * cancellation (matching the AI SDK path's behaviour).
 */
export async function* streamAgentReply({
  system,
  messages,
  signal,
  onSources,
  workshopId,
  referencedOutputIds,
}: StreamAgentReplyArgs): AsyncIterable<string> {
  const user = await getCurrentUser();
  if (!user || !user.access_token || user.expired) {
    throw new Error("Not signed in");
  }

  // Multi-client: the facilitator's active client scopes the chat's RAG
  // grounding + audit trail, same as every docs-api call.
  const acting = actAsClientId();
  const res = await fetch(`${BASE}/api/agent/chat`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      Authorization: `Bearer ${user.access_token}`,
      ...(acting ? { "X-Client-Id": acting } : {}),
    },
    body: JSON.stringify({
      system,
      messages: messages.map((m) => ({
        role: m.role,
        content: contentToString(m.content),
      })),
      ...(workshopId ? { workshopId } : {}),
      ...(referencedOutputIds?.length ? { referencedOutputIds } : {}),
    }),
    signal,
  });

  if (!res.ok) {
    // Surface the BFF's {detail} envelope when present (FastAPI style).
    let message = res.statusText;
    try {
      const payload = (await res.json()) as { detail?: string };
      if (payload.detail) message = payload.detail;
    } catch {
      /* non-JSON body — keep statusText */
    }
    throw new Error(message);
  }
  if (!res.body) throw new Error("agent response had no body");

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // SSE frames are separated by a blank line. Process complete frames;
      // keep the trailing partial in the buffer.
      let sep: number;
      while ((sep = buffer.indexOf("\n\n")) !== -1) {
        const frame = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        const parsed = parseFrame(frame);
        if (!parsed) continue;
        if ("sources" in parsed) {
          onSources?.(parsed.sources, parsed.grounding);
        } else if (parsed.delta) {
          yield parsed.delta;
        }
      }
    }
  } catch (err) {
    // A user-initiated abort surfaces as an AbortError on read() — treat it
    // as a clean stop, not a failure (the hook checks signal.aborted too).
    if (signal.aborted) return;
    throw err;
  } finally {
    reader.releaseLock();
  }
}

/** One parsed SSE frame: a text delta, a sources payload, or nothing. */
type ParsedFrame =
  | { delta: string }
  | { sources: AgentSource[]; grounding?: boolean }
  | null;

/** Parse one SSE frame. Returns a delta, a sources list, or null; throws on
 *  an error event. */
function parseFrame(frame: string): ParsedFrame {
  for (const line of frame.split("\n")) {
    const trimmed = line.trim();
    if (!trimmed.startsWith("data:")) continue;
    const payload = trimmed.slice("data:".length).trim();
    if (!payload) continue;
    let obj: {
      delta?: string;
      error?: string;
      done?: boolean;
      sources?: AgentSource[];
      grounding?: boolean;
    };
    try {
      obj = JSON.parse(payload);
    } catch {
      continue;
    }
    if (obj.error) throw new Error(obj.error);
    if (Array.isArray(obj.sources)) return { sources: obj.sources, grounding: obj.grounding };
    if (typeof obj.delta === "string") return { delta: obj.delta };
    // `done` and anything else carry no text.
  }
  return null;
}
